// ─────────────────────────────────────────────────────────────────────────────
// RexySpatialGraphThree — the WebGL layer of the Rexy Spatial Intelligence Graph.
//
// This is a NEW Three.js system. It is not an edit to RexyOrbThree, not an
// extension of the T-Rex silhouette, and not a replacement for the orb — it
// shares no code with any of them and imports nothing from them.
//
// WHAT IT KNOWS: buffers, positions and a camera. It has never heard of a file
// path, a silo, an API or a domain schema. Everything above it is the source's
// problem, which is what lets the real topology arrive at G2 without a renderer
// rewrite — and what lets a new domain, a new agent or a new colour arrive as a
// patch with no change to a single line in this file.
//
// THE FALLBACK IS LOUD, NOT SILENT. RexyOrbThree degrades to a static SVG, and a
// headless capture of that fallback looks plausible and is worthless as
// evidence. This component reports its mode — webgl2 / webgl1 / fallback — and
// the capture harness refuses to shoot unless it reads webgl2 with no fallback
// on screen.
//
// SCENE LAYERS, AND THE TWO THAT STILL DO NOT EXIST. Built here: micro nodes,
// hub nodes, base edges, selected edges, and the picking scene (inside
// GraphPicker). NOT built: an aggregate layer, because nothing in the lab is an
// aggregate yet — every node is hydrated — and a focus layer, because focus is
// an ATTRIBUTE on the existing points (aFocus) rather than a second copy of the
// geometry. A group nobody writes to is a lever someone eventually turns.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

import { GraphBuffers, NodeBufferChunk, EdgeBufferChunk } from './graphBuffers';
import { RexyGraphStore } from './graphStore';
import { GraphPositions } from './graphLayout';
import { GRAPH_BACKGROUND_INT, Rgb, resolveDomainColour } from './graphPalette';
import { GraphCameraDirector } from './graphCamera';
import { GraphPicker } from './graphPicking';
import { GraphZoomToPointer } from './graphZoomToPointer';
import {
  GraphLabelMode, LabelCandidate, LabelPolicyInput, PlacedLabel, labelCandidateIds, placeLabels,
} from './graphLabels';
import { SemanticLevel, focusRadiusFor, levelForDistance } from './graphLod';
import { visualForKind } from './graphRegistry';
import {
  EDGE_FRAGMENT_SHADER,
  EDGE_VERTEX_SHADER,
  NODE_FRAGMENT_SHADER,
  NODE_VERTEX_SHADER,
  SPARK_FRAGMENT_SHADER,
  SPARK_VERTEX_SHADER,
} from './graphShaders';
import { SparkBufferChunk } from './graphSparks';

export type GraphRenderMode = 'webgl2' | 'webgl1' | 'fallback';

export interface GraphStats {
  fps: number;
  drawCalls: number;
  points: number;
  lines: number;
  geometries: number;
  textures: number;
  programs: number;
}

export interface GraphVerification {
  hasCanvas: boolean;
  contextType: GraphRenderMode;
  contextLost: boolean;
  fallbackPresent: boolean;
  clearColorRoundTrip: boolean;
  litPixels: number;
  luminance: {
    mean: number; p50: number; p90: number; p99: number; max: number; totalEnergy: number;
  };
  drawCalls: number;
  points: number;
  lines: number;
  glVersion: string;
  glVendor: string;
  glRenderer: string;
}

export interface FocusRequest {
  id: string;
  /** Bumped by the caller to re-fire a focus on the node already focused. */
  nonce: number;
}

export interface RexySpatialGraphThreeProps {
  buffers: GraphBuffers;
  positions: GraphPositions;
  store: RexyGraphStore;
  selectedId: string | null;
  hoveredId: string | null;
  focusRequest: FocusRequest | null;
  sparks: SparkBufferChunk[];
  sparkOpacity: number;
  labelMode: GraphLabelMode;
  searchResultIds: string[];
  searchActive: boolean;
  edgeAlphaScale: number;
  nodeScale: number;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  onLevel: (level: SemanticLevel, normalised: number) => void;
  onStats?: (stats: GraphStats) => void;
  /** Reported so the Dev tab can show it without reaching into the renderer. */
  onLabelCount?: (count: number) => void;
  onMode?: (mode: GraphRenderMode) => void;
  /** Merged into window.__rexyGraphLab so the harness has ONE door. */
  labExtras?: Record<string, unknown>;
}

const CAMERA_FOV = 50;
const CAMERA_NEAR = 0.5;
const CAMERA_FAR = 20000;
const CAMERA_START: [number, number, number] = [0, 220, 2400];
// Stage B, owner-ruled. minDistance 2 is what lets the camera get INSIDE a file
// cloud rather than stopping at its shell.
const CONTROLS_MIN_DISTANCE = 2;
const CONTROLS_MAX_DISTANCE = 12000;

// Pick thresholds by pointer type. A finger is not a mouse: the flat 4px this
// replaces made a touch drag select whatever it started on.
const MOUSE_PICK_THRESHOLD_PX = 5;
const TOUCH_PICK_THRESHOLD_PX = 10;
const PEN_PICK_THRESHOLD_PX = 8;

function thresholdForPointerType(pointerType: string): number {
  if (pointerType === 'touch') return TOUCH_PICK_THRESHOLD_PX;
  if (pointerType === 'pen') return PEN_PICK_THRESHOLD_PX;
  return MOUSE_PICK_THRESHOLD_PX;
}
const MAX_PIXEL_RATIO = 2;
const NODE_REFERENCE_DISTANCE = 900;

/** Stage C/D ruled tunings. Micro is tight; hub is whiter and slightly larger. */
// G1.2 approved step: exposure and white-mix up, mid/halo/point-size UNCHANGED.
// The isolated passes measured the node layer at 375 lit pixels against the edge
// layer's 18,948 — brighter PINS, never larger sprites or wider halos.
const MICRO_TUNING = { mid: 0.18, halo: 0.025, white: 0.26, exposure: 1.28, maxPoint: 7.0 };
const HUB_TUNING = { mid: 0.26, halo: 0.075, white: 0.46, exposure: 1.35, maxPoint: 28.0 };
const SPARK_TUNING = { mid: 0.10, halo: 0.018, white: 0.12, exposure: 1.05, maxPoint: 3.5 };

/** Selection context, ruled. Unrelated fades but is never hidden. */
const UNRELATED_CONTEXT_ALPHA = 0.34;
const TWO_HOP_CONTEXT_ALPHA = 0.58;
const SELECTED_EDGE_ALPHA = 0.82;

/** Labels are re-projected at this cadence, never per frame. */
const LABEL_INTERVAL_MS = 100;
/**
 * Hover identification timing.
 *
 * HOVER = IDENTIFY, CLICK = SELECT, DOUBLE CLICK = ENTER. The dwell is what
 * stops a sweep through a dense borough from machine-gunning labels: the pointer
 * must REST on a node before it is named. The exit grace covers a single missed
 * pick frame, which would otherwise read as a flicker.
 */
const HOVER_INTERVAL_MS = 45; // ~22Hz
const HOVER_DWELL_MS = 160;
const HOVER_EXIT_MS = 80;
/** Hover stays suppressed this long after a camera gesture ends. */
const HOVER_RESUME_MS = 80;

/** Counts every WebGLRenderer ever constructed in this document. See below. */
let rendererInstanceCounter = 0;

function probeContext(): GraphRenderMode {
  try {
    const canvas = document.createElement('canvas');
    if (canvas.getContext('webgl2')) return 'webgl2';
    if (canvas.getContext('webgl')) return 'webgl1';
  } catch (err) {
    // A throwing getContext is a fallback, not a crash.
  }
  return 'fallback';
}

function buildNodeGeometry(chunk: NodeBufferChunk): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(chunk.position, 3));
  g.setAttribute('aColor', new THREE.BufferAttribute(chunk.aColor, 3));
  g.setAttribute('aSize', new THREE.BufferAttribute(chunk.aSize, 1));
  g.setAttribute('aAlpha', new THREE.BufferAttribute(chunk.aAlpha, 1));
  g.setAttribute('aImportance', new THREE.BufferAttribute(chunk.aImportance, 1));
  g.setAttribute('aSelected', new THREE.BufferAttribute(chunk.aSelected, 1));
  g.setAttribute('aHovered', new THREE.BufferAttribute(chunk.aHovered, 1));
  g.setAttribute('aContextAlpha', new THREE.BufferAttribute(chunk.aContextAlpha, 1));
  g.setAttribute('aPulsePhase', new THREE.BufferAttribute(chunk.aPulsePhase, 1));
  g.setAttribute('aKind', new THREE.BufferAttribute(chunk.aKind, 1));
  g.setAttribute('aPickId', new THREE.BufferAttribute(chunk.aPickId, 1));
  g.computeBoundingSphere();
  return g;
}

function buildEdgeGeometry(chunk: EdgeBufferChunk): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(chunk.position, 3));
  g.setAttribute('aEdgeColor', new THREE.BufferAttribute(chunk.colour, 3));
  g.setAttribute('aEdgeAlpha', new THREE.BufferAttribute(chunk.alpha, 1));
  g.setAttribute('aRelationKind', new THREE.BufferAttribute(chunk.relationKind, 1));
  g.computeBoundingSphere();
  return g;
}

function GraphFallbackTree({ store }: { store: RexyGraphStore }): React.ReactElement {
  const roots: string[] = [];
  store.forEachNode((node) => {
    if (!node.parentId || !store.getNode(node.parentId)) roots.push(node.id);
  });
  roots.sort();

  const renderNode = (id: string, depth: number): React.ReactElement | null => {
    const node = store.getNode(id);
    if (!node) return null;
    const children = store.childrenOf(id).slice().sort().slice(0, 40);
    return (
      <li key={id} style={{ margin: '2px 0' }}>
        <span style={{ color: '#cfe6ff' }}>{node.label}</span>
        <span style={{ color: '#5f7288', marginLeft: 8, fontSize: 11 }}>{node.kind}</span>
        {children.length > 0 && depth < 4 && (
          <ul style={{ listStyle: 'none', paddingLeft: 16, margin: 0 }}>
            {children.map((c) => renderNode(c, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div style={fallbackStyle} data-rexy-graph-fallback="true">
      <div style={fallbackNoticeStyle}>
        WEBGL UNAVAILABLE — structural fallback. This is the hierarchy, not the graph.
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>{roots.map((r) => renderNode(r, 0))}</ul>
    </div>
  );
}

export default function RexySpatialGraphThree(props: RexySpatialGraphThreeProps): React.ReactElement {
  const {
    buffers, positions, store, sparks, sparkOpacity, selectedId, hoveredId, focusRequest,
    labelMode, searchResultIds, searchActive, edgeAlphaScale, nodeScale,
    onSelect, onHover, onLevel, onStats, onMode, onLabelCount, labExtras,
  } = props;

  const hostRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [mode, setMode] = useState<GraphRenderMode>(() => probeContext());
  const [labels, setLabels] = useState<PlacedLabel[]>([]);
  /** The ONE hover feather. Null unless a node has been rested on. */
  /**
   * A body-level fixed layer for the feather.
   *
   * It used to render inside the canvas slot, which is `position: absolute;
   * z-index: 1` — a stacking context. The feather's own z-index 40 resolved
   * INSIDE it and could never rise above the vignette (z 2) or the dock layer
   * (z 3). Portalled to the document so its z-index means what it says.
   */
  const [hoverLayer, setHoverLayer] = useState<HTMLDivElement | null>(null);
  const [hoverFeather, setHoverFeather] = useState<{
    id: string; primary: string; secondary: string; colour: string; x: number; y: number;
  } | null>(null);

  const glRef = useRef<{
    renderer: THREE.WebGLRenderer;
    rendererInstance: number;
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    controls: OrbitControls;
    director: GraphCameraDirector;
    zoomToPointer: GraphZoomToPointer;
    picker: GraphPicker;
    graphRoot: THREE.Group;
    microNodeLayer: THREE.Group;
    hubNodeLayer: THREE.Group;
    baseEdgeLayer: THREE.Group;
    selectedEdgeLayer: THREE.Group;
    sparkLayer: THREE.Group;
    sparkMaterial: THREE.ShaderMaterial | null;
    initialFitDone: boolean;
    nodeMaterials: THREE.ShaderMaterial[];
    edgeMaterial: THREE.ShaderMaterial | null;
    selectedEdgeMaterial: THREE.ShaderMaterial | null;
    /** node id → the geometry and index whose aFocus to write. */
    focusIndex: { [id: string]: { geometry: THREE.BufferGeometry; index: number } };
    size: { width: number; height: number };
  } | null>(null);

  // Props the render loop reads. Kept in refs so changing one never rebuilds the
  // scene and never re-runs the mount effect.
  const liveRef = useRef({
    selectedId, hoveredId, labelMode, searchResultIds, searchActive,
    onLevel, onStats, onHover, onSelect,
  });
  liveRef.current = {
    selectedId, hoveredId, labelMode, searchResultIds, searchActive,
    onLevel, onStats, onHover, onSelect,
  };

  const verifyRef = useRef<((v: GraphVerification) => void) | null>(null);
  const levelRef = useRef<SemanticLevel>(0);
  const pickWarmupRef = useRef<{ ms: number; complete: boolean }>({ ms: 0, complete: false });
  const frameMetricsRef = useRef({ mean: 0, p50: 0, p95: 0, p99: 0, over25: 0, samples: 0 });
  const focusNodeRef = useRef<{ id: string; radius: number }>({ id: '', radius: 700 });

  useEffect(() => {
    const layer = document.createElement('div');
    layer.className = 'rexy-graph-hover-layer';
    layer.setAttribute('style',
      'position:fixed;inset:0;z-index:25;pointer-events:none;overflow:visible;');
    document.body.appendChild(layer);
    setHoverLayer(layer);
    return () => { document.body.removeChild(layer); };
  }, []);

  useEffect(() => {
    if (onMode) onMode(mode);
  }, [mode, onMode]);

  useEffect(() => {
    if (onLabelCount) onLabelCount(labels.length);
  }, [labels.length, onLabelCount]);

  // ── mount ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (mode === 'fallback') return;
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas) return;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        canvas, alpha: true, antialias: true, powerPreference: 'high-performance',
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[RexySpatialGraph] WebGLRenderer construction failed, falling back:', err);
      setMode('fallback');
      return;
    }
    rendererInstanceCounter++;

    const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
    renderer.setPixelRatio(ratio);
    renderer.setClearColor(GRAPH_BACKGROUND_INT, 1);
    const w0 = host.clientWidth || 1;
    const h0 = host.clientHeight || 1;
    renderer.setSize(w0, h0, false);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(CAMERA_FOV, w0 / h0, CAMERA_NEAR, CAMERA_FAR);
    camera.position.set(CAMERA_START[0], CAMERA_START[1], CAMERA_START[2]);

    const graphRoot = new THREE.Group();
    const microNodeLayer = new THREE.Group();
    const hubNodeLayer = new THREE.Group();
    const baseEdgeLayer = new THREE.Group();
    const selectedEdgeLayer = new THREE.Group();
    const sparkLayer = new THREE.Group();
    graphRoot.add(sparkLayer);
    graphRoot.add(baseEdgeLayer);
    graphRoot.add(selectedEdgeLayer);
    graphRoot.add(microNodeLayer);
    graphRoot.add(hubNodeLayer);
    scene.add(graphRoot);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 0, 0);
    controls.enableDamping = true;
    controls.dampingFactor = 0.065;
    controls.enableRotate = true;
    controls.rotateSpeed = 0.42;
    controls.enableZoom = true;
    controls.zoomSpeed = 0.92;
    controls.enablePan = true;
    controls.panSpeed = 0.82;
    controls.screenSpacePanning = true;
    controls.minDistance = CONTROLS_MIN_DISTANCE;
    controls.maxDistance = CONTROLS_MAX_DISTANCE;
    controls.minPolarAngle = 0;
    controls.maxPolarAngle = Math.PI;
    controls.autoRotate = false;
    controls.mouseButtons.LEFT = THREE.MOUSE.ROTATE;
    controls.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY;
    controls.mouseButtons.RIGHT = THREE.MOUSE.PAN;
    controls.touches.ONE = THREE.TOUCH.ROTATE;
    controls.touches.TWO = THREE.TOUCH.DOLLY_PAN;
    controls.update();

    glRef.current = {
      renderer,
      rendererInstance: rendererInstanceCounter,
      scene, camera, controls,
      director: new GraphCameraDirector(camera, controls),
      zoomToPointer: new GraphZoomToPointer(camera, controls, renderer.domElement),
      picker: new GraphPicker(ratio, NODE_REFERENCE_DISTANCE),
      graphRoot, microNodeLayer, hubNodeLayer, baseEdgeLayer, selectedEdgeLayer, sparkLayer,
      nodeMaterials: [], edgeMaterial: null, selectedEdgeMaterial: null, sparkMaterial: null,
      initialFitDone: false,
      focusIndex: {},
      size: { width: w0, height: h0 },
    };

    setMode(renderer.capabilities.isWebGL2 ? 'webgl2' : 'webgl1');

    // MANUAL INPUT ALWAYS WINS. Wheel, pointer-down and the controls' own start
    // event all stand a focus flight down from the exact frame it reached — no
    // snap to its start, no snap to its destination.
    const gl0 = glRef.current;
    if (gl0) gl0.zoomToPointer.onManualInput = () => gl0.director.cancel('helper');

    // AND a listener the graph owns directly, in capture phase, registered BEFORE
    // the helper's own. Relying solely on the helper callback left the wheel path
    // failing to cancel while the drag path worked; owning the cancel here makes
    // the order explicit — cancel, then anchor, then dolly.
    const cancelOnWheel = () => {
      const gl = glRef.current;
      if (gl) gl.director.cancel('wheel');
    };
    canvas.addEventListener('wheel', cancelOnWheel, { capture: true, passive: true });

    const applySize = () => {
      const gl = glRef.current;
      if (!gl) return;
      const w = host.clientWidth || 1;
      const h = host.clientHeight || 1;
      const r = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
      gl.renderer.setPixelRatio(r);
      gl.renderer.setSize(w, h, false);
      gl.camera.aspect = w / h;
      gl.camera.updateProjectionMatrix();
      gl.picker.setPixelRatio(r);
      gl.size.width = w;
      gl.size.height = h;
      for (let i = 0; i < gl.nodeMaterials.length; i++) {
        gl.nodeMaterials[i].uniforms.uPixelRatio.value = r;
      }
    };
    const observer = new ResizeObserver(applySize);
    observer.observe(host);
    applySize();

    let raf = 0;
    let warmupRaf = 0;
    let last = 0;
    let fps = 0;
    let statsAcc = 0;
    let labelAcc = 0;
    const clock = new THREE.Clock();
    const pixel = new Uint8Array(4);
    // Preallocated ring buffer. One float written per frame, no allocation, and
    // the percentiles are computed once a second from a reused scratch copy.
    const FRAME_SAMPLES = 120;
    const frameTimes = new Float32Array(FRAME_SAMPLES);
    const frameScratch = new Float32Array(FRAME_SAMPLES);
    let frameCursor = 0;
    let frameFilled = 0;

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      if (document.hidden && !verifyRef.current) return;

      const gl = glRef.current;
      if (!gl) return;

      const dt = last ? (now - last) / 1000 : 0;
      if (last) {
        frameTimes[frameCursor] = now - last;
        frameCursor = (frameCursor + 1) % FRAME_SAMPLES;
        if (frameFilled < FRAME_SAMPLES) frameFilled++;
      }
      last = now;
      if (dt > 0) fps = fps ? fps * 0.9 + (1 / dt) * 0.1 : 1 / dt;

      const t = clock.getElapsedTime();
      for (let i = 0; i < gl.nodeMaterials.length; i++) {
        gl.nodeMaterials[i].uniforms.uTime.value = t;
      }
      if (gl.sparkMaterial) gl.sparkMaterial.uniforms.uTime.value = t;

      if (!gl.director.update(now)) gl.controls.update();

      // Semantic level, relative to what the camera is looking at.
      const normalised = gl.director.distanceToTarget() / (focusNodeRef.current.radius || 1);
      const next = levelForDistance(normalised, levelRef.current);
      if (next !== levelRef.current) {
        levelRef.current = next;
        liveRef.current.onLevel(next, normalised);
      }

      const pending = verifyRef.current;
      let clearRoundTrip = false;
      if (pending) {
        const ctx = gl.renderer.getContext();
        gl.renderer.setClearColor(0x336699, 1);
        gl.renderer.clear(true, true, true);
        ctx.readPixels(0, 0, 1, 1, ctx.RGBA, ctx.UNSIGNED_BYTE, pixel);
        clearRoundTrip = pixel[0] === 0x33 && pixel[1] === 0x66 && pixel[2] === 0x99;
        gl.renderer.setClearColor(GRAPH_BACKGROUND_INT, 1);
      }

      gl.renderer.render(gl.scene, gl.camera);

      // PICK WARM-UP, one-shot, straight after the first real frame.
      // The rAF-scheduled version never completed — a discarded development
      // mount was cancelling it — so it now runs inline on this instance, once,
      // and can never belong to a renderer that no longer exists.
      if (!pickWarmupRef.current.complete) {
        const w0 = performance.now();
        gl.picker.pick(gl.renderer, gl.camera, 1, 1, gl.size.width, gl.size.height);
        pickWarmupRef.current = { ms: performance.now() - w0, complete: true };
      }

      if (pending) {
        verifyRef.current = null;
        pending(readVerification(gl.renderer, mode, clearRoundTrip));
      }

      // ── hover acquisition, on the clock ──────────────────────────────────
      // Runs whether or not the pointer is moving, which is the entire fix.
      if (now - lastHoverRef.current >= HOVER_INTERVAL_MS) {
        lastHoverRef.current = now;
        const ptr = pointerRef.current;
        const hs = hoverStateRef.current;

        if (!ptr.inside || now < cameraActiveRef.current) {
          if (hs.visibleId || hs.candidateId) clearHoverRef.current();
        } else {
          const id = pickRef.current(ptr.x, ptr.y);
          if (!id) {
            if (hs.visibleId && !hs.exitAt) hs.exitAt = now + HOVER_EXIT_MS;
            if (hs.exitAt && now >= hs.exitAt) clearHoverRef.current();
            hs.candidateId = null;
          } else {
            hs.exitAt = 0;
            if (id !== hs.candidateId) {
              hs.candidateId = id;
              hs.candidateSince = now;
            } else if (now - hs.candidateSince >= HOVER_DWELL_MS) {
              if (hs.visibleId !== id) {
                hs.visibleId = id;
                liveRef.current.onHover(id);
                setHoverFeather(describeHoverRef.current(id, ptr.x, ptr.y));
              } else {
                setHoverFeather((prev) =>
                  prev && (prev.x !== ptr.x || prev.y !== ptr.y) ? { ...prev, x: ptr.x, y: ptr.y } : prev);
              }
            }
          }
        }
        hoverTraceRef.current = {
          pointer: ptr.inside ? [Math.round(ptr.x), Math.round(ptr.y)] : null,
          suppressed: now < cameraActiveRef.current,
          candidate: hs.candidateId,
          candidateAgeMs: hs.candidateId ? Math.round(now - hs.candidateSince) : 0,
          visible: hs.visibleId,
        };
      }

      labelAcc += dt * 1000;
      if (labelAcc > LABEL_INTERVAL_MS) {
        labelAcc = 0;
        const next = projectLabels(gl, store, positions, liveRef.current);
        // Nothing selected in the default mode yields an empty list, and the
        // shared EMPTY_LABELS reference keeps React from re-rendering the label
        // layer ten times a second over a list that was already empty.
        setLabels(next.length ? next : EMPTY_LABELS);
      }

      statsAcc += dt;
      if (statsAcc > 0.5 && liveRef.current.onStats) {
        statsAcc = 0;
        const info = gl.renderer.info;
        frameScratch.set(frameTimes);
        const slice = frameScratch.subarray(0, frameFilled);
        Array.prototype.sort.call(slice, (a: number, b: number) => a - b);
        const q = (f: number) => (frameFilled ? slice[Math.min(frameFilled - 1, Math.floor(frameFilled * f))] : 0);
        let sum = 0;
        let over25 = 0;
        for (let i = 0; i < frameFilled; i++) { sum += slice[i]; if (slice[i] > 25) over25++; }
        frameMetricsRef.current = {
          mean: frameFilled ? sum / frameFilled : 0,
          p50: q(0.5), p95: q(0.95), p99: q(0.99), over25, samples: frameFilled,
        };
        liveRef.current.onStats({
          fps: Math.round(fps),
          drawCalls: info.render.calls,
          points: info.render.points,
          lines: info.render.lines,
          geometries: info.memory.geometries,
          textures: info.memory.textures,
          programs: info.programs ? info.programs.length : 0,
        });
      }
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(warmupRaf);
      canvas.removeEventListener('wheel', cancelOnWheel, { capture: true } as any);
      observer.disconnect();
      const gl = glRef.current;
      if (gl) {
        gl.director.cancel();
        gl.zoomToPointer.dispose();
        gl.picker.dispose();
        gl.controls.dispose();
        disposeGroup(gl.microNodeLayer);
        disposeGroup(gl.hubNodeLayer);
        disposeGroup(gl.baseEdgeLayer);
        disposeGroup(gl.selectedEdgeLayer);
        disposeGroup(gl.sparkLayer);
        gl.scene.remove(gl.graphRoot);
        gl.renderer.dispose();
      }
      glRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode === 'fallback']);

  // ── buffers → scene ────────────────────────────────────────────────────────
  useEffect(() => {
    const gl = glRef.current;
    if (!gl) return;

    // MICRO and HUB share the program and differ ONLY in uniforms. Hubs carry a
    // wider white core and a slightly stronger halo because they are few; micro
    // nodes stay tight because they are tens of thousands and their halos are
    // what would turn the galaxy into fog.
    const makeNodeMaterial = (tuning: {
      mid: number; halo: number; white: number; exposure: number; maxPoint: number;
    }) =>
      new THREE.ShaderMaterial({
        vertexShader: NODE_VERTEX_SHADER,
        fragmentShader: NODE_FRAGMENT_SHADER,
        uniforms: {
          uPixelRatio: { value: gl.renderer.getPixelRatio() },
          uReferenceDistance: { value: NODE_REFERENCE_DISTANCE },
          uTime: { value: 0 },
          uSizeMultiplier: { value: nodeScale },
          uMaxPointSize: { value: tuning.maxPoint },
          uMidWeight: { value: tuning.mid },
          uHaloWeight: { value: tuning.halo },
          uWhiteMix: { value: tuning.white },
          uExposure: { value: tuning.exposure },
        },
        transparent: true,
        depthTest: true, // a real volume: rear nodes sit behind front nodes
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

    const makeEdgeMaterial = (scale: number) =>
      new THREE.ShaderMaterial({
        vertexShader: EDGE_VERTEX_SHADER,
        fragmentShader: EDGE_FRAGMENT_SHADER,
        uniforms: { uAlphaScale: { value: scale } },
        transparent: true,
        depthTest: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

    const microMaterial = makeNodeMaterial(MICRO_TUNING);
    const hubMaterial = makeNodeMaterial(HUB_TUNING);
    const edgeMaterial = makeEdgeMaterial(edgeAlphaScale);
    const selectedEdgeMaterial = makeEdgeMaterial(12);

    gl.nodeMaterials = [microMaterial, hubMaterial];
    gl.edgeMaterial = edgeMaterial;
    gl.selectedEdgeMaterial = selectedEdgeMaterial;
    gl.focusIndex = {};

    const addNodes = (chunks: NodeBufferChunk[], layer: THREE.Group, material: THREE.ShaderMaterial) => {
      for (let i = 0; i < chunks.length; i++) {
        const geometry = buildNodeGeometry(chunks[i]);
        layer.add(new THREE.Points(geometry, material));
        for (let n = 0; n < chunks[i].count; n++) {
          gl.focusIndex[chunks[i].ids[n]] = { geometry, index: n };
        }
      }
    };
    addNodes(buffers.micro, gl.microNodeLayer, microMaterial);
    addNodes(buffers.hub, gl.hubNodeLayer, hubMaterial);

    for (let i = 0; i < buffers.edges.length; i++) {
      gl.baseEdgeLayer.add(new THREE.LineSegments(buildEdgeGeometry(buffers.edges[i]), edgeMaterial));
    }

    // ── edge sparks ─────────────────────────────────────────────────────────
    const sparkMaterial = new THREE.ShaderMaterial({
      vertexShader: SPARK_VERTEX_SHADER,
      fragmentShader: SPARK_FRAGMENT_SHADER,
      uniforms: {
        uPixelRatio: { value: gl.renderer.getPixelRatio() },
        uReferenceDistance: { value: NODE_REFERENCE_DISTANCE },
        uTime: { value: 0 },
        uOpacity: { value: sparkOpacity },
        uMaxPointSize: { value: SPARK_TUNING.maxPoint },
        uMidWeight: { value: SPARK_TUNING.mid },
        uHaloWeight: { value: SPARK_TUNING.halo },
        uWhiteMix: { value: SPARK_TUNING.white },
        uExposure: { value: SPARK_TUNING.exposure },
      },
      transparent: true,
      depthTest: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    gl.sparkMaterial = sparkMaterial;

    for (let i = 0; i < sparks.length; i++) {
      const chunk = sparks[i];
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(chunk.position, 3));
      geometry.setAttribute('aColor', new THREE.BufferAttribute(chunk.aColor, 3));
      geometry.setAttribute('aAlpha', new THREE.BufferAttribute(chunk.aAlpha, 1));
      geometry.setAttribute('aSize', new THREE.BufferAttribute(chunk.aSize, 1));
      geometry.setAttribute('aPulsePhase', new THREE.BufferAttribute(chunk.aPulsePhase, 1));
      geometry.computeBoundingSphere();
      gl.sparkLayer.add(new THREE.Points(geometry, sparkMaterial));
    }

    gl.picker.setChunks([buffers.micro, buffers.hub]);

    // ── initial fit, ONCE ──────────────────────────────────────────────────
    // The one automatic camera placement in the system. It runs on the first
    // completed layout and never again: no refit on selection, filter, level,
    // patch or dock — those are the owner's view, not ours to reset.
    if (!gl.initialFitDone && buffers.contentRadius > 0) {
      gl.initialFitDone = true;
      const fov = (gl.camera.fov * Math.PI) / 180;
      const distance = (buffers.contentRadius / Math.sin(fov / 2)) * 1.16;
      const dir = new THREE.Vector3()
        .subVectors(gl.camera.position, gl.controls.target)
        .normalize();
      if (dir.lengthSq() < 1e-6) dir.set(0, 0.1, 1).normalize();
      gl.controls.target.set(0, 0, 0);
      gl.camera.position.copy(dir.multiplyScalar(distance));
      gl.camera.updateProjectionMatrix();
      gl.controls.update();
    }

    return () => {
      const cur = glRef.current;
      if (!cur) return;
      disposeGroup(cur.microNodeLayer, true);
      disposeGroup(cur.hubNodeLayer, true);
      disposeGroup(cur.baseEdgeLayer, true);
      disposeGroup(cur.selectedEdgeLayer, true);
      disposeGroup(cur.sparkLayer, true);
      sparkMaterial.dispose();
      cur.sparkMaterial = null;
      microMaterial.dispose();
      hubMaterial.dispose();
      edgeMaterial.dispose();
      selectedEdgeMaterial.dispose();
      cur.nodeMaterials = [];
      cur.edgeMaterial = null;
      cur.selectedEdgeMaterial = null;
      cur.focusIndex = {};
    };
    // nodeScale/edgeAlphaScale are uniforms, applied in their own effect below —
    // moving a slider must not rebuild a buffer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buffers, sparks, mode]);

  // Sliders are uniform writes. No geometry, no allocation, no rebuild.
  useEffect(() => {
    const gl = glRef.current;
    if (!gl) return;
    if (gl.edgeMaterial) gl.edgeMaterial.uniforms.uAlphaScale.value = edgeAlphaScale;
    if (gl.sparkMaterial) gl.sparkMaterial.uniforms.uOpacity.value = sparkOpacity;
    for (let i = 0; i < gl.nodeMaterials.length; i++) {
      gl.nodeMaterials[i].uniforms.uSizeMultiplier.value = nodeScale;
    }
  }, [edgeAlphaScale, nodeScale, sparkOpacity, buffers, sparks]);

  // ── selection: emphasis attributes + neighbourhood context ─────────────────
  // Selection writes ATTRIBUTES, never geometry. The traversal that builds the
  // context mask runs here — once, on a selection change — and never in RAF.
  useEffect(() => {
    const gl = glRef.current;
    if (!gl) return;

    const writeAll = (chunks: NodeBufferChunk[], geometries: THREE.BufferGeometry[]) => {
      for (let c = 0; c < chunks.length; c++) {
        const geometry = geometries[c];
        if (!geometry) continue;
        (geometry.getAttribute('aSelected') as THREE.BufferAttribute).needsUpdate = true;
        (geometry.getAttribute('aHovered') as THREE.BufferAttribute).needsUpdate = true;
        (geometry.getAttribute('aContextAlpha') as THREE.BufferAttribute).needsUpdate = true;
      }
    };

    const geomOf = (layer: THREE.Group): THREE.BufferGeometry[] =>
      layer.children.map((child) => (child as THREE.Points).geometry as THREE.BufferGeometry);

    // Reset every dynamic channel first, so the previous selection's context
    // cannot survive into this one.
    const allChunks = buffers.micro.concat(buffers.hub);
    for (let c = 0; c < allChunks.length; c++) {
      allChunks[c].aSelected.fill(0);
      allChunks[c].aHovered.fill(0);
      allChunks[c].aContextAlpha.fill(selectedId ? UNRELATED_CONTEXT_ALPHA : 1);
    }

    if (selectedId) {
      // Direct neighbours at 1.0, two-hop at 0.58, everything else faded but
      // never hidden — the owner keeps the galaxy around him.
      const direct: { [id: string]: true } = {};
      const twoHop: { [id: string]: true } = {};
      const firstEdges = store.edgesOf(selectedId);
      for (let i = 0; i < firstEdges.length; i++) {
        const edge = store.getEdge(firstEdges[i]);
        if (!edge) continue;
        const other = edge.sourceId === selectedId ? edge.targetId : edge.sourceId;
        direct[other] = true;
      }
      const directIds = Object.keys(direct);
      for (let i = 0; i < directIds.length; i++) {
        const near = store.edgesOf(directIds[i]);
        for (let j = 0; j < near.length; j++) {
          const edge = store.getEdge(near[j]);
          if (!edge) continue;
          const other = edge.sourceId === directIds[i] ? edge.targetId : edge.sourceId;
          if (!direct[other] && other !== selectedId) twoHop[other] = true;
        }
      }

      for (let c = 0; c < allChunks.length; c++) {
        const chunk = allChunks[c];
        for (let i = 0; i < chunk.count; i++) {
          const id = chunk.ids[i];
          if (!id) continue;
          if (id === selectedId) {
            chunk.aSelected[i] = 1;
            chunk.aContextAlpha[i] = 1;
          } else if (direct[id]) {
            chunk.aContextAlpha[i] = 1;
          } else if (twoHop[id]) {
            chunk.aContextAlpha[i] = TWO_HOP_CONTEXT_ALPHA;
          }
          if (id === hoveredId) chunk.aHovered[i] = 1;
        }
      }
    } else if (hoveredId) {
      for (let c = 0; c < allChunks.length; c++) {
        const chunk = allChunks[c];
        for (let i = 0; i < chunk.count; i++) {
          if (chunk.ids[i] === hoveredId) chunk.aHovered[i] = 1;
        }
      }
    }

    writeAll(buffers.micro, geomOf(gl.microNodeLayer));
    writeAll(buffers.hub, geomOf(gl.hubNodeLayer));

    // The selected node's own relations, drawn bright in their own layer.
    disposeGroup(gl.selectedEdgeLayer, true);
    if (selectedId && gl.selectedEdgeMaterial) {
      const related = store.edgesOf(selectedId);
      const usable: Array<[number[], number[], Rgb, Rgb]> = [];
      for (let i = 0; i < related.length && usable.length < 4096; i++) {
        const edge = store.getEdge(related[i]);
        if (!edge) continue;
        const a = positions.get(edge.sourceId);
        const b = positions.get(edge.targetId);
        const sn = store.getNode(edge.sourceId);
        const tn = store.getNode(edge.targetId);
        if (!a || !b || !sn || !tn) continue;
        usable.push([
          a, b,
          resolveDomainColour(sn.domainId || sn.id, sn.colour).rgb,
          resolveDomainColour(tn.domainId || tn.id, tn.colour).rgb,
        ]);
      }
      if (usable.length) {
        const pos = new Float32Array(usable.length * 6);
        const col = new Float32Array(usable.length * 6);
        const alpha = new Float32Array(usable.length * 2);
        const rel = new Float32Array(usable.length * 2);
        for (let i = 0; i < usable.length; i++) {
          const [a, b, ca, cb] = usable[i];
          pos.set([a[0], a[1], a[2], b[0], b[1], b[2]], i * 6);
          col.set([ca[0], ca[1], ca[2], cb[0], cb[1], cb[2]], i * 6);
          alpha[i * 2] = SELECTED_EDGE_ALPHA;
          alpha[i * 2 + 1] = SELECTED_EDGE_ALPHA;
        }
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        geometry.setAttribute('aEdgeColor', new THREE.BufferAttribute(col, 3));
        geometry.setAttribute('aEdgeAlpha', new THREE.BufferAttribute(alpha, 1));
        geometry.setAttribute('aRelationKind', new THREE.BufferAttribute(rel, 1));
        geometry.computeBoundingSphere();
        gl.selectedEdgeLayer.add(new THREE.LineSegments(geometry, gl.selectedEdgeMaterial));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, hoveredId, buffers, positions, store]);

  // ── focus flight ───────────────────────────────────────────────────────────
  useEffect(() => {
    const gl = glRef.current;
    if (!gl || !focusRequest) return;
    const node = store.getNode(focusRequest.id);
    const p = positions.get(focusRequest.id);
    if (!node || !p) return;
    focusNodeRef.current = { id: node.id, radius: focusRadiusFor(node.kind) };
    gl.director.flyTo(p, node.kind, performance.now(), node.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest, positions, store]);

  // ── pointer ────────────────────────────────────────────────────────────────
  const lastHoverRef = useRef(0);

  /**
   * The last place the pointer was seen. THE DWELL IS DRIVEN FROM THE RENDER
   * LOOP, NOT FROM pointermove.
   *
   * This is the bug that made hover invisible on a real machine while every
   * automated test passed: a resting pointer stops firing move events, so the
   * "has this candidate been held for 160ms" check was only ever evaluated by
   * the next movement — and hovering IS resting. The forced-feather test drove
   * the DOM directly and never touched this path.
   */
  const pointerRef = useRef({ x: 0, y: 0, inside: false });
  const hoverStateRef = useRef({
    candidateId: null as string | null,
    candidateSince: 0,
    visibleId: null as string | null,
    exitAt: 0,
  });
  /** True while a camera gesture owns the pointer; hover does not acquire then. */
  const cameraActiveRef = useRef(0);
  /** Live chain trace for the Dev HUD: pointer → pick → candidate → visible. */
  const hoverTraceRef = useRef<{
    pointer: number[] | null; suppressed: boolean; candidate: string | null;
    candidateAgeMs: number; visible: string | null;
  }>({ pointer: null, suppressed: false, candidate: null, candidateAgeMs: 0, visible: null });
  // Stable handles so the render loop never closes over a stale callback.
  const pickRef = useRef<(x: number, y: number) => string | null>(() => null);
  const clearHoverRef = useRef<() => void>(() => {});
  const describeHoverRef = useRef<(id: string, x: number, y: number) => any>(() => null);

  const clearHover = useCallback(() => {
    const hs = hoverStateRef.current;
    if (!hs.visibleId && !hs.candidateId) return;
    hs.visibleId = null;
    hs.candidateId = null;
    hs.exitAt = 0;
    liveRef.current.onHover(null);
    setHoverFeather(null);
  }, []);


  const pickAt = useCallback((clientX: number, clientY: number): string | null => {
    const gl = glRef.current;
    const host = hostRef.current;
    if (!gl || !host) return null;
    const rect = host.getBoundingClientRect();
    return gl.picker.pick(
      gl.renderer, gl.camera,
      clientX - rect.left,
      // readRenderTargetPixels counts from the bottom; the pointer counts from
      // the top. Flipping here rather than in the picker keeps the picker in
      // GL coordinates throughout.
      rect.height - (clientY - rect.top),
      rect.width, rect.height,
    );
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    // Displacement is measured from the DOWN point, not from the last frame, so
    // a slow drift past the threshold still counts as a drag. Once true it never
    // goes back to false for that gesture.
    // Record where the pointer is; the loop does the rest.
    const p = pointerRef.current;
    p.x = e.clientX;
    p.y = e.clientY;
    p.inside = true;

    const g = gestureRef.current;
    if (g.id === e.pointerId && !g.moved) {
      if (Math.hypot(e.clientX - g.x, e.clientY - g.y) > thresholdForPointerType(g.type)) {
        g.moved = true;
      }
    }
  }, []);

  const handlePointerLeave = useCallback(() => {
    pointerRef.current.inside = false;
    clearHover();
  }, [clearHover]);

  pickRef.current = (x, y) => pickAt(x, y);
  clearHoverRef.current = clearHover;
  describeHoverRef.current = (id, x, y) => describeHover(store, id, x, y);

  const gestureRef = useRef({ id: -1, type: 'mouse', x: 0, y: 0, moved: false });

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    cameraActiveRef.current = performance.now() + HOVER_RESUME_MS;
    clearHover();
    gestureRef.current = {
      id: e.pointerId, type: e.pointerType, x: e.clientX, y: e.clientY, moved: false,
    };
  }, [clearHover]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    const g = gestureRef.current;
    const isGesture = g.id === e.pointerId;
    gestureRef.current = { id: -1, type: 'mouse', x: 0, y: 0, moved: false };

    // A camera move is never a selection. Anything past the pointer type's
    // threshold was navigation and ends silently.
    // The camera keeps the pointer for a moment after a gesture, so a drag's
    // release does not immediately name whatever it landed on.
    cameraActiveRef.current = performance.now() + HOVER_RESUME_MS;
    if (!isGesture || g.moved || e.button !== 0) return;

    liveRef.current.onSelect(pickAt(e.clientX, e.clientY));
  }, [pickAt]);

  const handlePointerCancel = useCallback(() => {
    gestureRef.current = { id: -1, type: 'mouse', x: 0, y: 0, moved: false };
  }, []);

  // Double click focuses. It is deliberately separate from selection: a single
  // click must never move the camera.
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    const id = pickAt(e.clientX, e.clientY);
    if (!id) return;
    const gl = glRef.current;
    const node = store.getNode(id);
    const p = positions.get(id);
    if (!gl || !node || !p) return;
    focusNodeRef.current = { id: node.id, radius: focusRadiusFor(node.kind) };
    gl.director.flyTo(p, node.kind, performance.now(), node.id);
  }, [pickAt, store, positions]);

  // ── the harness door ───────────────────────────────────────────────────────
  useEffect(() => {
    const w = window as any;
    w.__rexyGraphLab = Object.assign({}, labExtras, {
      mode,
      nodeCount: buffers.nodeCount,
      edgeCount: buffers.edgeCount,
      boundingRadius: buffers.boundingRadius,
      contentRadius: buffers.contentRadius,
      extents: buffers.extents,
      layoutDigest: buffers.layoutDigest,
      colourDigest: buffers.colourDigest,
      // A FUNCTION, not a snapshot. The first cut froze this at the value the
      // object happened to be built with, so three captures at three held levels
      // all reported L1 while the pixels plainly disagreed. Read it live or do
      // not publish it.
      cameraLevel: () => levelRef.current,

      // The instance number of the WebGLRenderer currently on screen. It is the
      // arithmetic proof that a data patch did NOT rebuild the renderer: if the
      // number is the same before and after, the same GL context drew both.
      rendererInstance: glRef.current ? glRef.current.rendererInstance : 0,

      getCamera: () => {
        const gl = glRef.current;
        if (!gl) return null;
        const p = gl.camera.position;
        const t = gl.controls.target;
        return { position: [p.x, p.y, p.z], target: [t.x, t.y, t.z], distance: p.distanceTo(t) };
      },
      setCamera: (position: number[], target?: number[]) => {
        const gl = glRef.current;
        if (!gl) return false;
        gl.camera.position.set(position[0], position[1], position[2]);
        if (target) gl.controls.target.set(target[0], target[1], target[2]);
        gl.controls.update();
        return true;
      },
      pickAtScreen: (x: number, y: number) => {
        const gl = glRef.current;
        if (!gl) return null;
        return gl.picker.pick(gl.renderer, gl.camera, x, gl.size.height - y, gl.size.width, gl.size.height);
      },
      labelCount: () => labels.length,
      pickWarmup: () => pickWarmupRef.current,
      frameMetrics: () => frameMetricsRef.current,
      hoverAt: (x: number, y: number) => {
        // Harness door: acquire exactly as a rested pointer would, dwell included.
        const gl = glRef.current;
        if (!gl) return null;
        const id = gl.picker.pick(gl.renderer, gl.camera, x, gl.size.height - y, gl.size.width, gl.size.height);
        if (!id) { setHoverFeather(null); return null; }
        hoverStateRef.current.visibleId = id;
        setHoverFeather(describeHover(store, id, x, y));
        return id;
      },
      hoverTrace: () => hoverTraceRef.current,
      hoverState: () => ({
        visibleId: hoverStateRef.current.visibleId,
        feathers: document.querySelectorAll('[data-rexy-hover-feather]').length,
      }),
      clearHover: () => { hoverStateRef.current.visibleId = null; setHoverFeather(null); },
      zoomStats: () => {
        const gl = glRef.current;
        return gl ? gl.zoomToPointer.getStats() : null;
      },
      resetZoomStats: () => {
        const gl = glRef.current;
        if (gl) gl.zoomToPointer.resetStats();
      },
      controlsConfig: () => {
        const gl = glRef.current;
        if (!gl) return null;
        const c = gl.controls;
        return {
          minDistance: c.minDistance, maxDistance: c.maxDistance,
          panSpeed: c.panSpeed, zoomSpeed: c.zoomSpeed, rotateSpeed: c.rotateSpeed,
          dampingFactor: c.dampingFactor, autoRotate: c.autoRotate,
        };
      },
      isFlying: () => {
        const gl = glRef.current;
        return gl ? gl.director.isFlying : false;
      },
      // EVIDENCE DOORS. Layer visibility for isolated passes, and a pick-latency
      // probe — both read the system as built; neither changes how it renders.
      setLayerVisibility: (which: string) => {
        const gl = glRef.current;
        if (!gl) return false;
        const nodes = which === 'nodes' || which === 'combined';
        const edges = which === 'edges' || which === 'combined';
        const sparks = which === 'sparks' || which === 'combined';
        gl.microNodeLayer.visible = nodes;
        gl.hubNodeLayer.visible = nodes;
        gl.baseEdgeLayer.visible = edges;
        gl.selectedEdgeLayer.visible = edges;
        gl.sparkLayer.visible = sparks;
        return true;
      },
      pickTrace: () => {
        const gl = glRef.current;
        return gl ? { slow: gl.picker.slowPicks, last: gl.picker.timing } : null;
      },
      pickLatency: (samples: number) => {
        const gl = glRef.current;
        if (!gl) return null;
        const times: number[] = [];
        let hits = 0;
        for (let i = 0; i < samples; i++) {
          // A deterministic sweep across the viewport — same points every run.
          const x = ((i * 37) % 97) / 97 * gl.size.width;
          const y = ((i * 53) % 89) / 89 * gl.size.height;
          const t0 = performance.now();
          const id = gl.picker.pick(gl.renderer, gl.camera, x, gl.size.height - y, gl.size.width, gl.size.height);
          times.push(performance.now() - t0);
          if (id) hits++;
        }
        const over = (t: number) => times.filter((v) => v > t).length;
        const buckets = { over10: over(10), over20: over(20), over50: over(50), over100: over(100), over500: over(500) };
        times.sort((a, b) => a - b);
        const at = (q: number) => times[Math.min(times.length - 1, Math.floor(times.length * q))];
        return {
          samples: times.length, hits, buckets,
          mean: times.reduce((a, b) => a + b, 0) / (times.length || 1),
          p50: at(0.5), p90: at(0.9), p95: at(0.95), p99: at(0.99), p995: at(0.995),
          max: times[times.length - 1],
        };
      },
      programCount: () => {
        const gl = glRef.current;
        return gl && gl.renderer.info.programs ? gl.renderer.info.programs.length : 0;
      },

      verify: (): Promise<GraphVerification> =>
        new Promise((resolve) => {
          const gl = glRef.current;
          if (!gl || mode === 'fallback') {
            resolve(fallbackVerification(mode));
            return;
          }
          verifyRef.current = resolve;
        }),
    });
    return () => {
      delete (window as any).__rexyGraphLab;
    };
  }, [mode, buffers, labels.length, labExtras]);

  if (mode === 'fallback') {
    return (
      <div ref={hostRef} style={hostStyle}>
        <GraphFallbackTree store={store} />
      </div>
    );
  }

  return (
    <div
      ref={hostRef}
      style={hostStyle}
      onPointerMove={handlePointerMove}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      onPointerCancel={handlePointerCancel}
      onDoubleClick={handleDoubleClick}
    >
      <style>{FEATHER_KEYFRAMES}</style>
      <canvas ref={canvasRef} style={canvasStyle} />
      {hoverFeather && hoverLayer && createPortal(<HoverFeather feather={hoverFeather} />, hoverLayer)}
      <div style={labelLayerStyle}>
        {labels.map((l) => (
          <span
            key={l.id}
            style={{
              position: 'absolute',
              left: l.x + 8,
              top: l.y - 8,
              color: l.colour,
              opacity: l.emphasis ? 1 : 0.72,
              fontSize: l.emphasis ? 12 : 10.5,
              letterSpacing: 0.8,
              whiteSpace: 'nowrap',
              textShadow: '0 0 6px rgba(0,0,0,0.9)',
            }}
          >
            {l.label}
          </span>
        ))}
      </div>
    </div>
  );
}

const EMPTY_LABELS: PlacedLabel[] = [];

/**
 * What a hover feather says — identity only.
 *
 * Trust tiers, ids, paths and relation counts belong in the Inspector. A hover is
 * the owner asking "what is that", not "tell me everything".
 */
function describeHover(store: RexyGraphStore, id: string, x: number, y: number) {
  const node = store.getNode(id);
  if (!node) return null;
  const colour = resolveDomainColour(node.domainId || node.id, node.colour).hex;

  const domainNode = node.domainId ? store.getNode(node.domainId) : undefined;
  const domainLabel = domainNode ? domainNode.label : '';
  const agentNode = node.agentId && node.agentId !== node.id ? store.getNode(node.agentId) : undefined;

  let secondary = '';
  if (node.kind === 'city-hall') secondary = 'CITY HALL';
  else if (node.kind === 'domain') {
    const agents = store.childrenOf(node.id).length;
    secondary = 'DOMAIN' + (agents ? ' · ' + agents + ' AGENTS' : '');
  } else if (node.kind === 'agent') secondary = 'AGENT · ' + domainLabel;
  else {
    const owner = agentNode ? agentNode.label : domainLabel;
    secondary = (owner ? owner + ' · ' : '') + domainLabel;
    if (secondary === domainLabel + ' · ' + domainLabel) secondary = domainLabel;
  }

  return { id, primary: node.label, secondary, colour, x, y };
}

function HoverFeather({ feather }: {
  feather: { id: string; primary: string; secondary: string; colour: string; x: number; y: number };
}): React.ReactElement {
  // Clamped so the feather never leaves the viewport, and flipped to the left of
  // the pointer when it would cross the right edge.
  const flipX = feather.x > window.innerWidth - 220;
  const flipY = feather.y < 70;
  return (
    <div
      style={{
        position: 'fixed',
        left: feather.x + (flipX ? -12 : 12),
        top: feather.y + (flipY ? 14 : -10),
        transform: flipX ? 'translateX(-100%)' : undefined,
        pointerEvents: 'none',
        zIndex: 40,
        padding: '6px 9px',
        borderRadius: 6,
        background: 'rgba(3, 8, 16, 0.88)',
        border: '1px solid ' + feather.colour + '4d',
        boxShadow: '0 0 14px ' + feather.colour + '22',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        animation: 'rexyGraphFeather 140ms ease-out',
      }}
      data-rexy-hover-feather="true"
    >
      <div style={{ color: '#ffffff', fontSize: 12, letterSpacing: 0.4 }}>{feather.primary}</div>
      {feather.secondary && (
        <div style={{ color: 'rgba(207,230,255,0.55)', fontSize: 10, letterSpacing: 0.8, marginTop: 1 }}>
          {feather.secondary}
        </div>
      )}
    </div>
  );
}

function disposeGroup(group: THREE.Group, keepMaterials?: boolean): void {
  for (let i = group.children.length - 1; i >= 0; i--) {
    const child = group.children[i] as THREE.Points | THREE.LineSegments;
    child.geometry.dispose();
    if (!keepMaterials) (child.material as THREE.Material).dispose();
    group.remove(child);
  }
}

/**
 * Label candidates, priority-ordered. Only nodes the camera could plausibly be
 * reading get considered — City Hall, the boroughs, and whatever is inside the
 * current focus — so the projection cost stays proportional to what is on screen
 * rather than to the size of the graph.
 */
interface LabelRenderContext {
  camera: THREE.PerspectiveCamera;
  size: { width: number; height: number };
}

function projectLabels(
  gl: LabelRenderContext,
  store: RexyGraphStore,
  positions: GraphPositions,
  live: LabelPolicyInput,
): PlacedLabel[] {
  // WHO gets a label is graphLabels' decision; this only resolves and projects
  // the ids it returns. With nothing selected that list is empty and the whole
  // pass costs one function call.
  const ids = labelCandidateIds(
    live,
    (id) => {
      const n = store.getNode(id);
      return n ? n.kind : undefined;
    },
    (id) => store.childrenOf(id),
    (id) => {
      const n = store.getNode(id);
      return n ? n.parentId : undefined;
    },
  );
  if (!ids.length) return EMPTY_LABELS;

  const candidates: LabelCandidate[] = [];
  for (let i = 0; i < ids.length; i++) {
    const node = store.getNode(ids[i].id);
    const p = positions.get(ids[i].id);
    if (!node || !p) continue;
    const colour = resolveDomainColour(node.domainId || node.id, node.colour);
    candidates.push({
      id: node.id,
      label: node.label,
      position: p,
      kind: node.kind,
      colour: visualForKind(node.kind).colourRole === 'leaf' ? '#93a7bd' : colour.hex,
      priority: ids[i].priority,
    });
  }

  return placeLabels(candidates, gl.camera, gl.size.width, gl.size.height, live.selectedId);
}

function readVerification(
  renderer: THREE.WebGLRenderer,
  mode: GraphRenderMode,
  clearColorRoundTrip: boolean,
): GraphVerification {
  const ctx = renderer.getContext();

  // The read covers the WHOLE drawing buffer and subsamples in JS. Reading a
  // small corner region instead would report zero for a graph sitting in the
  // middle of the view, which is exactly the false negative this exists to
  // avoid. Drawing-buffer size, not CSS size: readPixels works in device pixels.
  const size = new THREE.Vector2();
  renderer.getDrawingBufferSize(size);
  const w = Math.max(1, Math.floor(size.x));
  const h = Math.max(1, Math.floor(size.y));
  const buf = new Uint8Array(w * h * 4);
  ctx.readPixels(0, 0, w, h, ctx.RGBA, ctx.UNSIGNED_BYTE, buf);

  // Coverage AND energy. C/D.2 could only count pixels above a threshold, which
  // measures how much of the frame a layer touches — long lines inevitably touch
  // more than small points. Luminance is what the eye actually receives.
  const step = 4;
  let lit = 0;
  const lum: number[] = [];
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      const i = (y * w + x) * 4;
      const l = buf[i] * 0.2126 + buf[i + 1] * 0.7152 + buf[i + 2] * 0.0722;
      if (buf[i] > 12 || buf[i + 1] > 16 || buf[i + 2] > 24) {
        lit++;
        lum.push(l);
      }
    }
  }
  lum.sort((a, b) => a - b);
  const q = (f: number) => (lum.length ? lum[Math.min(lum.length - 1, Math.floor(lum.length * f))] : 0);
  const luminance = {
    mean: lum.length ? lum.reduce((a, b) => a + b, 0) / lum.length : 0,
    p50: q(0.5), p90: q(0.9), p99: q(0.99), max: lum.length ? lum[lum.length - 1] : 0,
    // Total energy over the sampled grid — the number that actually compares layers.
    totalEnergy: lum.reduce((a, b) => a + b, 0),
  };

  const debugInfo = (ctx as any).getExtension('WEBGL_debug_renderer_info');
  const info = renderer.info;

  return {
    hasCanvas: !!renderer.domElement,
    contextType: mode,
    contextLost: ctx.isContextLost(),
    fallbackPresent: !!document.querySelector('[data-rexy-graph-fallback]'),
    clearColorRoundTrip,
    litPixels: lit,
    luminance,
    drawCalls: info.render.calls,
    points: info.render.points,
    lines: info.render.lines,
    glVersion: String(ctx.getParameter(ctx.VERSION)),
    glVendor: String(debugInfo ? ctx.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : ctx.getParameter(ctx.VENDOR)),
    glRenderer: String(debugInfo ? ctx.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : ctx.getParameter(ctx.RENDERER)),
  };
}

function fallbackVerification(mode: GraphRenderMode): GraphVerification {
  return {
    hasCanvas: false, contextType: mode, contextLost: true, fallbackPresent: true,
    clearColorRoundTrip: false, litPixels: 0,
    luminance: { mean: 0, p50: 0, p90: 0, p99: 0, max: 0, totalEnergy: 0 },
    drawCalls: 0, points: 0, lines: 0,
    glVersion: '', glVendor: '', glRenderer: '',
  };
}

const FEATHER_KEYFRAMES = `
@keyframes rexyGraphFeather {
  from { opacity: 0; transform: translateY(2px); }
  to   { opacity: 1; transform: translateY(0); }
}`;

const hostStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  touchAction: 'none',
  overscrollBehavior: 'contain',
  userSelect: 'none',
  WebkitUserSelect: 'none',
};

const canvasStyle: React.CSSProperties = { display: 'block', width: '100%', height: '100%' };

const labelLayerStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

const fallbackStyle: React.CSSProperties = {
  position: 'absolute', inset: 0, overflow: 'auto', padding: 24,
  fontSize: 13, lineHeight: 1.6, color: '#9fb4cc',
};

const fallbackNoticeStyle: React.CSSProperties = {
  marginBottom: 16, padding: '8px 12px',
  border: '1px solid rgba(255,77,87,0.45)', color: '#ff8f96',
  letterSpacing: 1, fontSize: 12,
};
